import LegalLayout, { LegalH2 } from "../components/LegalLayout";
import useSEO from "../hooks/useSEO";
import { LEGAL } from "../config";
import { openPreferences } from "../lib/consent";
import { SlidersHorizontal } from "lucide-react";

const COOKIES = [
  { name: "cookie_consent_v1", provider: "Sito (tecnico)", purpose: "Memorizza le tue scelte sui cookie", duration: "Persistente (localStorage)" },
  { name: "Cookie di sessione / autenticazione", provider: "Sito (tecnico)", purpose: "Accesso all'area riservata CRM (solo amministratore)", duration: "Sessione / fino a 7 giorni" },
  { name: "_ga, _ga_*", provider: "Google Analytics", purpose: "Statistiche aggregate sull'uso del sito", duration: "Fino a 24 mesi" },
  { name: "_gcl_*, conversione Ads", provider: "Google Ads", purpose: "Misurazione conversioni e remarketing", duration: "Fino a 90 giorni" },
  { name: "_fbp", provider: "Meta Pixel", purpose: "Misurazione campagne e remarketing", duration: "Fino a 90 giorni" },
];

export default function CookiePolicy() {
  useSEO({
    title: "Cookie Policy | Muratore Imbianchino Roma",
    description: "Informativa estesa sui cookie utilizzati dal sito e su come gestire il consenso.",
    path: "/cookie-policy",
  });

  return (
    <LegalLayout title="Cookie Policy" updated={LEGAL.updated}>
      <p>
        Questa Cookie Policy spiega cosa sono i cookie, quali utilizziamo e come puoi
        gestire il tuo consenso. Integra la <a className="text-navy underline underline-offset-2" href="/privacy">Privacy Policy</a>.
      </p>

      <LegalH2>1. Cosa sono i cookie</LegalH2>
      <p>
        I cookie sono piccoli file di testo che i siti salvano sul dispositivo
        dell'utente. Insieme a tecnologie simili (es. localStorage) permettono al sito
        di funzionare, di ricordare le preferenze e, previo consenso, di raccogliere
        statistiche o misurare le campagne pubblicitarie.
      </p>

      <LegalH2>2. Cookie tecnici / necessari</LegalH2>
      <p>
        Indispensabili al funzionamento del sito e alla memorizzazione delle tue scelte
        sui cookie. Vengono installati senza bisogno di consenso (art. 122 Codice
        Privacy). Senza questi cookie il sito non funziona correttamente.
      </p>

      <LegalH2>3. Cookie analitici</LegalH2>
      <p>
        Utilizziamo Google Analytics per raccogliere statistiche aggregate sull'utilizzo
        del sito. Questi cookie vengono installati <strong>solo dopo il tuo consenso</strong>.
      </p>

      <LegalH2>4. Cookie di marketing / profilazione</LegalH2>
      <p>
        Utilizziamo Google Ads e Meta Pixel per misurare l'efficacia delle campagne e
        per attività di remarketing. Questi cookie vengono installati <strong>solo dopo il tuo consenso</strong>.
      </p>

      <LegalH2>5. Elenco dei cookie</LegalH2>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-surface text-navy">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Cookie</th>
              <th className="text-left font-semibold px-4 py-3">Fornitore</th>
              <th className="text-left font-semibold px-4 py-3">Finalità</th>
              <th className="text-left font-semibold px-4 py-3">Durata</th>
            </tr>
          </thead>
          <tbody>
            {COOKIES.map((c) => (
              <tr key={c.name} className="border-t border-gray-100 align-top">
                <td className="px-4 py-3 font-medium text-ink/80">{c.name}</td>
                <td className="px-4 py-3 text-ink/65">{c.provider}</td>
                <td className="px-4 py-3 text-ink/65">{c.purpose}</td>
                <td className="px-4 py-3 text-ink/65">{c.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink/50">Le durate sono indicative e stabilite dai rispettivi fornitori; alcuni cookie sono installati solo dopo il consenso e solo se le relative funzionalità sono attive.</p>

      <LegalH2>6. Come gestire il consenso</LegalH2>
      <p>
        Puoi modificare o revocare in qualsiasi momento le tue scelte sui cookie tramite
        il pulsante qui sotto:
      </p>
      <button
        onClick={openPreferences}
        data-testid="cookie-policy-manage"
        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_-8px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark transition-colors"
      >
        <SlidersHorizontal size={16} /> Gestisci preferenze cookie
      </button>
      <p className="mt-2">
        In alternativa puoi gestire o eliminare i cookie dalle impostazioni del tuo
        browser. Per i cookie di terze parti puoi consultare anche:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><a className="text-navy underline underline-offset-2" href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">Google — Cookie</a> e <a className="text-navy underline underline-offset-2" href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">opt-out Google Analytics</a></li>
        <li><a className="text-navy underline underline-offset-2" href="https://www.facebook.com/policies/cookies/" target="_blank" rel="noopener noreferrer">Meta — Cookie</a></li>
      </ul>

      <LegalH2>7. Titolare e contatti</LegalH2>
      <p>
        Titolare del trattamento: <strong>{LEGAL.owner}</strong> — {LEGAL.taxIdLabel}: {LEGAL.taxId} — {LEGAL.address}.
        Per qualsiasi richiesta scrivi a{" "}
        <a className="text-navy underline underline-offset-2" href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>.
      </p>
    </LegalLayout>
  );
}
